import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_PROFESSIONAL_SERVICES, DELETE_PROFESSIONAL_SERVICE, DEACTIVATE_PROFESSIONAL_SERVICE, REACTIVATE_PROFESSIONAL_SERVICE } from '../../graphql/professional-services';
import { CardComponent } from '../../components/CardComponent';
import { ListRowComponent } from '../../components/ListRowComponent';

const ProfessionalServices = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [services, setServices] = useState({});
    const [sortBy, setSortBy] = useState('companyName');
    const [viewMode, setViewMode] = useState('grid');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [activeFilter, setActiveFilter] = useState('ALL');
    
    const query = useQuery(GET_PROFESSIONAL_SERVICES, {
        variables: {
            pageIndex: 0,
            pageSize: 20,
            orderAsc: true,
            orderField: 'companyName',
            filters: {}
        },
        onCompleted: (data) => {
            setServices(data?.professionalServices);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const filters = {};
            
            // Apply type filter
            if (typeFilter !== 'ALL') {
                filters.type = typeFilter;
            }
            
            // Apply active status filter
            if (activeFilter !== 'ALL') {
                filters.active = activeFilter === 'ACTIVE';
            }
            
            // Apply search filter
            if (searchTerm.trim()) {
                filters.search = searchTerm;
            }

            const { data } = await query.refetch({
                pageIndex: 0,
                pageSize: 20,
                orderAsc: true,
                orderField: sortBy,
                filters: filters
            });
            setServices(data?.professionalServices);
        };

        const debounceTimer = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, sortBy, typeFilter, activeFilter, query]);

    const [deleteService] = useMutation(DELETE_PROFESSIONAL_SERVICE, {
        onCompleted: () => {
            query.refetch();
        },
        onError: (error) => {
            console.error('Error deleting professional service:', error);
            alert('Failed to delete professional service. Please try again.');
        }
    });

    const [deactivateService] = useMutation(DEACTIVATE_PROFESSIONAL_SERVICE, {
        onCompleted: () => {
            query.refetch();
        },
        onError: (error) => {
            console.error('Error deactivating service:', error);
            alert('Failed to deactivate service. Please try again.');
        }
    });

    const [reactivateService] = useMutation(REACTIVATE_PROFESSIONAL_SERVICE, {
        onCompleted: () => {
            query.refetch();
        },
        onError: (error) => {
            console.error('Error reactivating service:', error);
            alert('Failed to reactivate service. Please try again.');
        }
    });

    const handleDelete = (id, companyName) => {
        if (window.confirm(`Are you sure you want to delete ${companyName}? This action cannot be undone.`)) {
            deleteService({ variables: { id } });
        }
    };

    const handleToggleActive = (id, companyName, currentStatus) => {
        const action = currentStatus ? 'deactivate' : 'reactivate';
        if (window.confirm(`Are you sure you want to ${action} ${companyName}?`)) {
            if (currentStatus) {
                deactivateService({ 
                    variables: { 
                        id, 
                        reason: `Deactivated via Professional Services management on ${new Date().toLocaleDateString()}` 
                    } 
                });
            } else {
                reactivateService({ variables: { id } });
            }
        }
    };

    const sortOptions = [
        { key: "companyName", value: "Sort by Company Name" },
        { key: "type", value: "Sort by Service Type" },
        { key: "tradingName", value: "Sort by Trading Name" },
        { key: "createdAt", value: "Sort by Date Added" }
    ];

    const serviceTypes = [
        { key: "ALL", value: "All Service Types" },
        { key: "ATTORNEY", value: "Attorney" },
        { key: "BOND_ORIGINATOR", value: "Bond Originator" },
        { key: "CONVEYANCER", value: "Conveyancer" },
        { key: "ELECTRICIAN", value: "Electrician" },
        { key: "PLUMBER", value: "Plumber" },
        { key: "CONTRACTOR", value: "Contractor" },
        { key: "INSPECTOR", value: "Inspector" },
        { key: "SURVEYOR", value: "Surveyor" },
        { key: "OTHER", value: "Other" }
    ];

    const activeStatusOptions = [
        { key: "ALL", value: "All Status" },
        { key: "ACTIVE", value: "Active Only" },
        { key: "INACTIVE", value: "Inactive Only" }
    ];

    const getServiceTypeLabel = (type) => {
        const typeMap = {
            ATTORNEY: 'Attorney',
            BOND_ORIGINATOR: 'Bond Originator',
            CONVEYANCER: 'Conveyancer',
            ELECTRICIAN: 'Electrician',
            PLUMBER: 'Plumber',
            CONTRACTOR: 'Contractor',
            INSPECTOR: 'Inspector',
            SURVEYOR: 'Surveyor',
            OTHER: 'Other'
        };
        return typeMap[type] || type;
    };

    const getPerformanceColor = (rating) => {
        if (!rating) return 'bg-gray-100 text-gray-800';
        if (rating >= 4.5) return 'bg-green-100 text-green-800';
        if (rating >= 4.0) return 'bg-blue-100 text-blue-800';
        if (rating >= 3.5) return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('companyName');
        setTypeFilter('ALL');
        setActiveFilter('ALL');
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    if (query.loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (query.error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {query.error.message}</span>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2z" />
                        </svg>
                        Professional Services
                    </h1>
                    <p className="text-gray-600">Manage legal, financial, and professional service providers</p>
                </div>
                <Link
                    to="/sales/professional-services/add"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                    <span>Add Professional Service</span>
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            placeholder="Company name, trading name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>

                    {/* Service Type Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            {serviceTypes.map(type => (
                                <option key={type.key} value={type.key}>{type.value}</option>
                            ))}
                        </select>
                    </div>

                    {/* Active Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={activeFilter}
                            onChange={(e) => setActiveFilter(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            {activeStatusOptions.map(status => (
                                <option key={status.key} value={status.key}>{status.value}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sort */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            {sortOptions.map(option => (
                                <option key={option.key} value={option.key}>{option.value}</option>
                            ))}
                        </select>
                    </div>

                    {/* Reset & View Mode */}
                    <div className="flex items-end space-x-2">
                        <button
                            onClick={resetFilters}
                            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                            Reset
                        </button>
                        <div className="flex border border-gray-300 rounded-md">
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'} border-r border-gray-300`}
                            >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/>
                                </svg>
                            </button>
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
                            >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Services Grid/List */}
            <div className={viewMode === 'list' ? 'space-y-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
                {services?.items?.map(service => {
                    const ServiceCardContent = () => (
                        <div className="space-y-3">
                            {/* Basic Info */}
                            <div>
                                <div className="text-sm font-medium text-gray-900">
                                    {service.tradingName && service.tradingName !== service.companyName 
                                        ? `${service.tradingName} (${service.companyName})`
                                        : service.companyName
                                    }
                                </div>
                                <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {getServiceTypeLabel(service.type)}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        service.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {service.active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="text-sm text-gray-600">
                                {service.primaryContact && (
                                    <div>
                                        <strong>{service.primaryContact.firstName} {service.primaryContact.lastName}</strong>
                                        {service.primaryContact.title && <span className="text-gray-500"> - {service.primaryContact.title}</span>}
                                    </div>
                                )}
                                {service.primaryContact?.email && (
                                    <div className="text-xs text-gray-500">{service.primaryContact.email}</div>
                                )}
                                {service.primaryContact?.cellphone && (
                                    <div className="text-xs text-gray-500">{service.primaryContact.cellphone}</div>
                                )}
                            </div>

                            {/* Performance */}
                            {service.performance && (
                                <div className="flex flex-wrap gap-2">
                                    {service.performance.clientSatisfaction && (
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(service.performance.clientSatisfaction)}`}>
                                            ★ {service.performance.clientSatisfaction.toFixed(1)}
                                        </span>
                                    )}
                                    {service.performance.dealsCompleted && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            {service.performance.dealsCompleted} deals
                                        </span>
                                    )}
                                    {service.performance.completionRate && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            {(service.performance.completionRate * 100).toFixed(0)}% completion
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Specializations & Service Areas */}
                            {service.specializations && service.specializations.length > 0 && (
                                <div>
                                    <div className="text-xs font-medium text-gray-700 mb-1">Specializations:</div>
                                    <div className="flex flex-wrap gap-1">
                                        {service.specializations.slice(0, 3).map((spec, index) => (
                                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                {spec}
                                            </span>
                                        ))}
                                        {service.specializations.length > 3 && (
                                            <span className="text-xs text-gray-500">+{service.specializations.length - 3} more</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {service.serviceAreas && service.serviceAreas.length > 0 && (
                                <div>
                                    <div className="text-xs font-medium text-gray-700 mb-1">Service Areas:</div>
                                    <div className="text-xs text-gray-600">
                                        {service.serviceAreas.slice(0, 2).join(', ')}
                                        {service.serviceAreas.length > 2 && ` +${service.serviceAreas.length - 2} more`}
                                    </div>
                                </div>
                            )}

                            {/* Registration Info */}
                            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                {service.registrationNumber && (
                                    <span>Reg: {service.registrationNumber}</span>
                                )}
                                {service.licenseNumber && (
                                    <span>Lic: {service.licenseNumber}</span>
                                )}
                                {service.vatNumber && (
                                    <span>VAT: {service.vatNumber}</span>
                                )}
                            </div>
                        </div>
                    );

                    const componentProps = {
                        cardId: service.id,
                        deleteCallback: () => handleDelete(service.id, service.companyName),
                        editCallback: (id) => window.location.href = `/sales/professional-services/${id}/edit`,
                        detailsCallback: (id) => window.location.href = `/sales/professional-services/${id}`,
                        headerImageText: service.companyName?.substring(0, 2).toUpperCase() || 'PS',
                        headerTitle: service.companyName,
                        headerColor: service.active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700',
                        children: <ServiceCardContent />,
                        customActions: [{
                            label: service.active ? 'Deactivate' : 'Activate',
                            callback: () => handleToggleActive(service.id, service.companyName, service.active),
                            className: service.active
                                ? 'border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500'
                                : 'border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500'
                        }]
                    };

                    return viewMode === 'list' ? (
                        <ListRowComponent key={service.id} {...componentProps} />
                    ) : (
                        <CardComponent key={service.id} {...componentProps} />
                    );
                })}
            </div>

            {/* Empty State */}
            {services?.items?.length === 0 && (
                <div className="text-center py-12">
                    <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2z"/>
                    </svg>
                    <p className="text-gray-500 text-lg">No professional services found</p>
                    <p className="text-gray-400 text-sm">Add your first professional service provider to get started</p>
                    <Link
                        to="/sales/professional-services/add"
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                        Add Professional Service
                    </Link>
                </div>
            )}

            {/* Statistics */}
            {services?.items?.length > 0 && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Total Services</p>
                                <p className="text-2xl font-bold text-gray-900">{services?.items?.length || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Active</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {services?.items?.filter(s => s.active).length || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Service Types</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {new Set(services?.items?.map(s => s.type)).size || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Avg Rating</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {services?.items?.length > 0 
                                        ? (services.items
                                            .filter(s => s.performance?.clientSatisfaction)
                                            .reduce((acc, s) => acc + s.performance.clientSatisfaction, 0) / 
                                           services.items.filter(s => s.performance?.clientSatisfaction).length).toFixed(1)
                                        : '0.0'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfessionalServices;